import { Session } from '@supabase/supabase-js';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { AUTH_TIMEOUT_MS } from '../constants';
import { db } from '../services/db';
import { User } from '../types';

interface AuthContextType {
    currentUser: User | null;
    session: Session | null;
    loadingAuth: boolean;
    pendingConfirmationEmail: string | null;
    login: (email: string, pass: string) => Promise<void>;
    register: (email: string, pass: string, displayName: string, username: string) => Promise<void>;
    logout: () => Promise<void>;
    updateProfile: (updatedUser: User) => Promise<void>;
    setPendingConfirmationEmail: (email: string | null) => void;
    refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [currentUser, setCurrentUser] = useState<User | null>(null);
    const [session, setSession] = useState<Session | null>(null);
    const [loadingAuth, setLoadingAuth] = useState(true);
    const [pendingConfirmationEmail, setPendingConfirmationEmail] = useState<string | null>(null);

    // -- INIT --
    useEffect(() => {
        const init = async () => {
            try {
                db.init(); // Seed if empty
                const user = await db.getSession();
                const session = await db.getRawSession();
                if (user) {
                    setCurrentUser(user);
                }
                if (session) {
                    setSession(session);
                }
            } catch (error) {
                console.error("AuthContext: Init failed", error);
            } finally {
                setLoadingAuth(false);
            }
        };
        init();
    }, []);


    const login = async (email: string, pass: string) => {
        try {
            // Create a timeout promise
            const timeoutPromise = new Promise((_, reject) => {
                setTimeout(() => {
                    reject(new Error("Sign in timed out. Please check your network connection and retry."));
                }, AUTH_TIMEOUT_MS);
            });

            // Race the login against the timeout
            await Promise.race([
                db.login(email, pass),
                timeoutPromise
            ]);
            
            // If we get here, login succeeded before timeout
            const user = await db.getSession();
            const session = await db.getRawSession();
            if (user) {
                setCurrentUser(user);
            }
            if (session) {
                setSession(session);
            }
        } catch (e: any) {
            console.error("AuthContext: login failed", e);
            if (e.message && e.message.includes("Email not confirmed")) {
                setPendingConfirmationEmail(email);
                return;
            }
            throw e;
        }
    };

    const register = async (email: string, pass: string, displayName: string, username: string) => {
        const user = await db.register(email, pass, displayName, username);
        const session = await db.getRawSession();
        setCurrentUser(user);
        if (session) setSession(session);
    };

    const logout = async () => {
        await db.logout();
        setCurrentUser(null);
        setSession(null);
    };

    const updateProfile = async (updatedUser: User) => {
        const newUser = await db.updateProfile(updatedUser);
        setCurrentUser(newUser);
    };

    const refreshUser = async () => {
        const user = await db.getSession();
        const session = await db.getRawSession();
        if (user) setCurrentUser(user);
        if (session) setSession(session);
    };

    return (
        <AuthContext.Provider value={{
            currentUser,
            session,
            loadingAuth,
            pendingConfirmationEmail,
            login,
            register,
            logout,
            updateProfile,
            setPendingConfirmationEmail,
            refreshUser
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
